const PRELOADER_ID = 'wt-preloader';

const HIDE_ANIMATION_MS = 450;
const MIN_SHOW_MS = 150;
const MAX_WAIT_MS = 2500;

function getPreloader() {
  return document.getElementById(PRELOADER_ID);
}

function showPreloader(/** @type {string} */ reason) {
  const el = getPreloader();
  if (!el) return;

  el.classList.remove('wt-preloader--hidden');
  el.removeAttribute('hidden');
  el.setAttribute('aria-hidden', 'false');

  try {
    document.documentElement.classList.add('wt-preloader-active');
  } catch (e) {
    // no-op
  }

  if (reason) {
    try {
      el.dataset.shownReason = reason;
    } catch (e) {
      // no-op
    }
  }

  try {
    window.dispatchEvent(new CustomEvent('wt:preloader:shown', { detail: { reason } }));
  } catch (e) {
    // no-op
  }
}

function hidePreloader(/** @type {string} */ reason) {
  const el = getPreloader();
  if (!el) return;

  if (el.classList.contains('wt-preloader--hidden')) return;

  el.classList.add('wt-preloader--hidden');
  el.setAttribute('aria-hidden', 'true');

  try {
    document.documentElement.classList.remove('wt-preloader-active');
  } catch (e) {
    // no-op
  }

  if (reason) {
    try {
      el.dataset.hiddenReason = reason;
    } catch (e) {
      // no-op
    }
  }

  try {
    window.dispatchEvent(new CustomEvent('wt:preloader:hidden', { detail: { reason } }));
  } catch (e) {
    // no-op
  }

  window.setTimeout(() => {
    try {
      el.setAttribute('hidden', '');
    } catch (e) {
      // no-op
    }
  }, HIDE_ANIMATION_MS);
}

function initPreloader() {
  const el = getPreloader();
  if (!el) return;

  // In theme editor we avoid blocking interactions.
  if (document.documentElement.classList.contains('shopify-design-mode')) {
    hidePreloader('design_mode');
    return;
  }

  showPreloader('init');

  const startedAt = performance.now();
  let hidden = false;

  const hideWhenAllowed = (/** @type {string} */ reason) => {
    if (hidden) return;
    hidden = true;
    const elapsed = performance.now() - startedAt;
    const waitMore = Math.max(0, MIN_SHOW_MS - elapsed);
    window.setTimeout(() => hidePreloader(reason), waitMore);
  };

  /**
   * @param {EventTarget} target
   * @param {string} type
   * @param {number | null | undefined} timeoutMs
   * @returns {Promise<void>}
   */
  const waitForEventOnce = (target, type, timeoutMs) =>
    new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        try { target.removeEventListener(type, onEvent); } catch (e) {}
        resolve(undefined);
      };
      const onEvent = () => finish();
      try { target.addEventListener(type, onEvent, { once: true, passive: true }); } catch (e) { resolve(undefined); return; }
      if (timeoutMs != null) window.setTimeout(finish, timeoutMs);
    });

  /**
   * @param {Element | null} mediaEl
   * @param {number | null | undefined} timeoutMs
   * @returns {Promise<void>}
   */
  const waitForMediaReady = (mediaEl, timeoutMs) =>
    new Promise((resolve) => {
      if (!mediaEl) { resolve(undefined); return; }

      const isImg = mediaEl.tagName === 'IMG';
      const isVideo = mediaEl.tagName === 'VIDEO';

      if (isImg) {
        const img = /** @type {HTMLImageElement} */ (mediaEl);
        if (img.complete) {
          try {
            if (img.decode) {
              img
                .decode()
                .then(() => resolve(undefined))
                .catch(() => resolve(undefined));
              return;
            }
          } catch (e) {}
          resolve(undefined);
          return;
        }
        img.addEventListener('load', () => resolve(undefined), { once: true });
        img.addEventListener('error', () => resolve(undefined), { once: true });
        if (timeoutMs != null) window.setTimeout(() => resolve(undefined), timeoutMs);
        return;
      }

      if (isVideo) {
        const v = /** @type {HTMLVideoElement} */ (mediaEl);
        if (v.readyState >= 2) { resolve(undefined); return; }
        v.addEventListener('loadeddata', () => resolve(undefined), { once: true });
        v.addEventListener('canplay', () => resolve(undefined), { once: true });
        v.addEventListener('error', () => resolve(undefined), { once: true });
        if (timeoutMs != null) window.setTimeout(() => resolve(undefined), timeoutMs);
        return;
      }

      resolve(undefined);
    });

  const waitForHeroVideo = async () => {
    const heroRoot = document.querySelector('[id^="Hero-"]');
    if (!heroRoot) return;
    const v = heroRoot.querySelector('video');
    if (!v) return;

    // Ready enough to show: first frame available.
    await waitForMediaReady(v, 1200);

    // If it actually starts playing quickly, even better.
    await Promise.race([
      waitForEventOnce(v, 'playing', 300),
      waitForEventOnce(v, 'play', 300),
    ]);
  };

  const waitForFirstPortfolioMasonry = async () => {
    const firstGallery = document.querySelector('section[data-wt-gallery-root]');
    if (!firstGallery) return;

    // Wait for first meaningful media in the first gallery.
    const media = firstGallery.querySelector('video, img');
    await waitForMediaReady(media, 1200);
  };

  const isHome = (() => {
    const main = document.getElementById('MainContent');
    const tpl = main && main.getAttribute('data-template');
    return !!(tpl && (tpl.includes('index') || tpl.includes('home')));
  })();

  const criticalReady = (async () => {
    // Ensure DOM is parsed so selectors exist.
    if (document.readyState === 'loading') {
      await waitForEventOnce(document, 'DOMContentLoaded', 1200);
    }

    if (isHome) {
      await waitForHeroVideo();
      return;
    }

    await waitForFirstPortfolioMasonry();
  })();

  Promise.race([
    criticalReady.then(() => 'critical_ready'),
    waitForEventOnce(window, 'load', MAX_WAIT_MS).then(() => 'load_or_timeout'),
    /** @type {Promise<string>} */
    (new Promise((resolve) => window.setTimeout(() => resolve('max_wait'), MAX_WAIT_MS))),
  ]).then((reason) => hideWhenAllowed(String(reason)));

  // Safety: never keep user stuck on overlay (e.g. blocked 3rd party requests)
  const timeoutMs = Number(el.dataset.timeoutMs || 5000);
  window.setTimeout(() => hideWhenAllowed('timeout'), timeoutMs);

  // If the page is restored from BFCache, we should not keep the overlay.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      hidePreloader('bfcache');
    }
  });

  // Show preloader only when the browser is actually leaving the current page.
  // (Not on click, because user may not navigate due to preventDefault, JS routing, etc.)
  const showOnLeave = (/** @type {string} */ reason) => {
    showPreloader(reason);
  };

  window.addEventListener('beforeunload', () => showOnLeave('beforeunload'));
  window.addEventListener('pagehide', () => showOnLeave('pagehide'));

  // If it is already loaded (very fast pages)
  if (document.readyState === 'complete') {
    window.setTimeout(() => {
      hidePreloader('readyState_complete');
    }, 0);
  }
}

initPreloader();
